import { useState, useEffect } from 'react';

import { DeeplinkKeys } from '@/consts/deepLinks';
import { configureProject } from '@/utils/project/configureProject';
import { setAppStore } from '@/store';
import { getShouldSkipAutoGeneration } from '@/utils/storage/autoGeneration';
import { extractUrlParams } from '@/utils/ui/extractUrlParams';
import { InputType, PreviewMediaType } from '@/types';

import type { IProject, IInput, Preset } from '@/types';

type UseProjectInitResult = {
  isLoading: boolean;
  project: IProject | null;
  jsonUrl: string | null;
};

// Helper to create a pseudo-preset from a remote project JSON
const createPresetFromProject = (project: IProject): Preset => {
  const inputs = project.inputs || [];

  // Separate text and upload inputs
  const textItems = inputs.filter(
    (input: IInput) => input.type === InputType.TEXT
  );
  const uploadItems = inputs.filter(
    (input: IInput) =>
      input.type === InputType.IMAGE || input.type === InputType.VIDEO
  );

  // Map layout previewMedia to Preset's video/image arrays
  const previewMedia = project.layout?.sidebar?.header?.previewMedia;

  let video: Preset['video'] = [];
  let image: Preset['image'] = [];

  if (previewMedia?.src) {
    const media = {
      url: previewMedia.src,
      width: previewMedia.width || 0,
      height: previewMedia.height || 0,
    };

    if (previewMedia.type === PreviewMediaType.VIDEO) {
      video = [{ media: { ...media, poster: previewMedia.poster } }];
    } else {
      image = [{ media }];
    }
  }

  return {
    flowId: project.webhookId,
    type: project.resultType || PreviewMediaType.IMAGE,
    title: previewMedia?.title || '',
    description: previewMedia?.description || '',
    textItems,
    uploadItems,
    video,
    image,
  };
};

const useProjectInit = (): UseProjectInitResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [project, setProject] = useState<IProject | null>(null);
  const [jsonUrl, setJsonUrl] = useState<string | null>(null);

  useEffect(() => {
    // Skip if task_id is present - notification handler will handle navigation
    const { [DeeplinkKeys.taskId]: taskId } = extractUrlParams([
      DeeplinkKeys.taskId,
    ]);

    if (taskId) {
      setIsLoading(false);
      return;
    }

    const loadProject = async () => {
      try {
        const result = await configureProject();

        setProject(result.project);
        setJsonUrl(result.jsonUrl);

        // Check if we should skip auto-generation for this URL
        const shouldSkip = result.jsonUrl
          ? getShouldSkipAutoGeneration(result.jsonUrl)
          : false;

        console.log('[useProjectInit] shouldSkip:', shouldSkip);

        // Note: presetId deeplinks set isRemoteProject in usePresetDeeplink hook
        if (result.jsonUrl) {
          setAppStore({
            isRemoteProject: true,
            shouldSkipAutoGeneration: shouldSkip,
          });
        }

        // If project is loaded from JSON (presetJsonUrl), create pseudo-preset
        if (result.project) {
          const pseudoPreset = createPresetFromProject(result.project);

          setAppStore({
            selectedPreset: pseudoPreset,
            inputs: result.project.inputs || [],
            toolId: result.project.drive?.toolId,
            folderName: result.project.drive?.folderName,
            screen: 'create',
            hasAutoSelectedPreset: true,
          });
        }
      } catch {
        // Project loading failed
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, []);

  return { isLoading, project, jsonUrl };
};

export { useProjectInit };
